import LegalLayout from "./LegalLayout";

const S = ({ n, title, children, testid }) => (
  <section data-testid={testid} className="mt-12">
    <h2 className="font-display text-xl font-medium tracking-tight text-white">
      <span className="mr-3 text-sm font-light text-cyan-300/70">{n}</span>
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#C5CBD5]">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" updated="Last updated: October 2026" testid="privacy-page">
      <S n="01" title="Who We Are" testid="privacy-s1">
        <p>
          This Privacy Policy is issued by AIROBOTCAMSTUDIO LTD (UK Company No. 17410621),
          registered office: 71–75 Shelton Street, Covent Garden, London, United Kingdom, WC2H 9JQ.
          For privacy questions contact info@airobotcamstudio.com or airobotcamstudio@gmail.com.
          AIROBOTCAMSTUDIO LTD is the data controller for personal information collected through
          this website. We handle personal data in accordance with the UK GDPR and the Data
          Protection Act 2018.
        </p>
      </S>

      <S n="02" title="What We Collect" testid="privacy-s2">
        <p>This website collects personal information only through the forms you choose to submit:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li><strong className="text-white">Contact form</strong> — name, email address, subject and your message;</li>
          <li><strong className="text-white">Beta interest form</strong> — name, email address, country and any optional message;</li>
          <li><strong className="text-white">Early access registration</strong> — first name, last name, email address, an optional display name, and your optional marketing preference;</li>
          <li><strong className="text-white">Investor interest forms</strong> — name, email address, investor type or intended investment amount, phone number and company/organisation where voluntarily provided, country of residence, and your message;</li>
          <li><strong className="text-white">Technical/security data</strong> — information reasonably required to operate and secure the website, such as IP addresses used for rate limiting and abuse prevention.</li>
        </ul>
        <p>
          We do not collect special category data through our forms.
        </p>
        <p>
          We operate a privacy-friendly, first-party analytics system on our own infrastructure.
          It sets no cookies, stores nothing in your browser and does not track you across other
          websites. It records only the page or section visited, the referring website, an
          approximate device category (mobile, tablet or desktop) and a coarse region (derived
          from your browser's time zone, or country where the hosting network provides it),
          together with an anonymous identifier that rotates daily and cannot be linked back to
          you.
        </p>
      </S>

      <S n="03" title="Why We Process It" testid="privacy-s3">
        <ul className="list-disc space-y-2 pl-5">
          <li>Responding to contact enquiries;</li>
          <li>Managing beta interest registrations and future testing communications;</li>
          <li>Managing investor enquiries and expressions of interest;</li>
          <li>Operating and securing the website, and preventing spam and abuse;</li>
          <li>Understanding aggregate website usage (pages and sections visited, referring websites, countries and device categories) so we can improve the site;</li>
          <li>Meeting applicable legal obligations.</li>
        </ul>
        <p>
          Depending on the context, our lawful bases are your consent (given when you submit a
          form), our legitimate interests in operating and securing the website and responding to
          you, steps towards a contract, and legal obligations.
        </p>
      </S>

      <S n="04" title="Service Providers" testid="privacy-s4">
        <p>We use a small number of service providers to operate this website:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li><strong className="text-white">Resend</strong> — email delivery infrastructure used to route website form submissions and notifications to our team;</li>
          <li><strong className="text-white">LemonSlice</strong> — powers the optional live AI companion (Aria) preview on our homepage. If you choose to start a session, your voice and any messages you send are processed by LemonSlice to generate the companion's responses. Sessions are entirely optional and only begin when you open the widget;</li>
          <li><strong className="text-white">Hosting and database providers</strong> — used to store form submissions and serve the website.</li>
        </ul>
        <p>We do not sell personal information and do not share it for advertising purposes.</p>
        <p>
          Our analytics are operated entirely first-party on our own servers — no third-party
          analytics provider (such as Google Analytics) receives any data.
        </p>
      </S>

      <S n="05" title="International Processing" testid="privacy-s5">
        <p>
          Some service providers (such as Resend and LemonSlice) may process data outside
          the United Kingdom. Where this occurs, processing is carried out under appropriate
          safeguards consistent with UK data protection law, such as adequacy regulations or
          standard contractual clauses.
        </p>
      </S>

      <S n="06" title="Retention and Security" testid="privacy-s6">
        <p>
          We keep form submissions for as long as reasonably needed for the purpose they were
          provided — for example, beta and investor interest records are kept while the relevant
          programme remains active — after which they are deleted or anonymised. We apply
          reasonable technical and organisational measures, including server-side credential
          storage, encrypted transport and access controls. No method of transmission or storage is
          completely secure, but we work to protect your information.
        </p>
      </S>

      <S n="07" title="Your Rights" testid="privacy-s7">
        <p>
          Under the UK GDPR you have rights including access, rectification, erasure, restriction,
          objection and data portability, and the right to withdraw consent where processing is
          based on consent. To exercise any right, contact info@airobotcamstudio.com. You also have
          the right to complain to the UK Information Commissioner's Office (ICO).
        </p>
      </S>

      <S n="08" title="Cookies" testid="privacy-s8">
        <p>
          This website sets no tracking cookies and uses no analytics or advertising cookies. Our
          analytics system is cookieless by design: instead of any browser storage, it uses an
          anonymous identifier that rotates daily and cannot identify you. Optional analytics are
          only activated after you give consent through our cookie banner, and rejecting keeps
          them disabled. Your consent choice itself is stored in your browser's local storage so
          that we can honour it — this is strictly necessary for the consent system to function.
          Full details, including how to change or withdraw consent, are in our Cookie Policy.
        </p>
      </S>

      <S n="09" title="Changes to This Policy" testid="privacy-s9">
        <p>
          We may update this Privacy Policy as the website and platform develop. The current
          version will always be available on this page with its revision date.
        </p>
      </S>
    </LegalLayout>
  );
}
